/**
 * Settings dialog · Sync tab — design_handoff_integrations_page frame 21.
 * Auto-sync + "how far back to read" are staged behind this tab's own Save
 * changes button; Full re-sync/Disconnect are immediate actions (their own
 * confirm dialogs, not staged).
 *
 * "How far back to read" renders the three options but is NOT wired up:
 * the frame's copy assumes it controls how far back sync reads, but the
 * only matching backend field (pull_events_ahead) is documented as "days
 * ahead" (future), not "days back" — a real mismatch flagged in the build
 * plan pending confirmation from whoever owns the sync worker. Shipping a
 * guessed mapping risks silently misconfiguring a real sync window, so this
 * stays visual-only (selection doesn't affect the Save payload) and the
 * whole control is disabled, with "Last 3 months" fixed as the shown
 * default, until that confirmation happens.
 */
import React, { useCallback } from "react";
import Toggle from "../../../theme/untitled/Toggle/Toggle";
import Button from "../../../theme/untitled/Button/Button";
import Tabs from "../../../theme/untitled/Tabs/Tabs";
import Icon from "../../../theme/untitled/icons/Icon";
import { INTEGRATION_NAMES } from "../../../../../consts";
import styles from "./IntegrationSettingsDialog.module.scss";

const LOOKBACK_OPTIONS = [
  { key: "all", label: "Everything" },
  { key: "3m", label: "Last 3 months" },
  { key: "today", label: "From today" },
];

const SyncTab = ({
  integration,
  autoSync,
  onAutoSyncChange,
  lookback,
  onLookbackChange,
  onFullResync,
  onDisconnect,
}) => {
  const providerName = INTEGRATION_NAMES[integration.type];

  const handleFullResync = useCallback(
    () => onFullResync(integration),
    [onFullResync, integration]
  );
  const handleDisconnect = useCallback(
    () => onDisconnect(integration),
    [onDisconnect, integration]
  );

  return (
    <div>
      <div className={styles.srow}>
        <div className={styles.srowText}>
          <label className={styles.rowLabel}>Auto-sync</label>
          <p>
            Changes in {providerName} appear in Revisual within a few
            minutes. Turn it off to freeze events where they are.
          </p>
        </div>
        <div className={styles.srowAction}>
          <Toggle checked={autoSync} onChange={onAutoSyncChange} label="Auto-sync" />
        </div>
      </div>

      <div className={styles.srow}>
        <div className={styles.srowText}>
          <label className={styles.rowLabel}>How far back to read</label>
          <p>Older events are ignored, which keeps the first sync short.</p>
          <Tabs
            variant="segmented"
            items={LOOKBACK_OPTIONS}
            value={lookback}
            onChange={onLookbackChange}
            disabled
            className={styles.lookbackTabs}
          />
        </div>
      </div>

      <div className={styles.srow}>
        <div className={styles.srowText}>
          <label className={styles.rowLabel}>Full re-sync</label>
          <p>
            Wipes your synced data and syncs everything from scratch. Your
            widgets won&rsquo;t show items from this source while it runs.
          </p>
        </div>
        <div className={styles.srowAction}>
          <Button
            hierarchy="secondaryGray"
            size="sm"
            iconLeading={<Icon name="RefreshCcw01" size={16} />}
            onClick={handleFullResync}
          >
            Start
          </Button>
        </div>
      </div>

      <div className={styles.srow}>
        <div className={styles.srowText}>
          <label className={styles.rowLabel}>Disconnect</label>
          <p>Stops syncing and removes the data this account brought in.</p>
        </div>
        <div className={styles.srowAction}>
          <Button hierarchy="destructive" size="sm" onClick={handleDisconnect}>
            Disconnect
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SyncTab;
